import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import classNames from 'classnames/bind';
import { Gift, Heart, LogOut, User, Users, X } from 'react-feather';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { themeRootId } from 'containers/ThemeProvider/ThemeProvider';
import { actions } from 'coupon-common/src/modules/auth';

import { RouteNames } from '../../containers/App/routeNames';
import styles from './CustomerSidebar.module.scss';

/**
 * We can not use react-css-modules here because of portal rendering — same
 * reasoning as components/Modal/Modal.js. Use classnames/bind + className
 * instead of styleName throughout this file.
 */
const cx = classNames.bind(styles);

/**
 * Same portal approach as components/Modal/Modal.js (render into
 * themeRootId via a detached div), kept local here since this needs a
 * dedicated div ref per-mount the same way Modal's usePortal does.
 */
function usePortal(id) {
  const rootElemRef = useRef(document.createElement('div'));

  useEffect(
    function setupElement() {
      const rootElement = rootElemRef.current;
      const parentElem = document.querySelector(`#${id}`);

      parentElem.appendChild(rootElement);

      return function removeElement() {
        rootElement.remove();
      };
    },
    [id],
  );

  return rootElemRef.current;
}

const CustomerSidebar = ({ isOpen, onRequestClose, dispatch }) => {
  const target = usePortal(themeRootId);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      disableBodyScroll(target);
    } else {
      enableBodyScroll(target);
    }
    return () => {
      enableBodyScroll(target);
    };
  }, [isOpen, target]);

  const logout = () => {
    onRequestClose();
    dispatch(actions.logout());
    // FIX: redirect to customer login on logout.
    // Uses RouteNames.CUSTOMER_LOGIN to stay consistent with the Link
    // targets below — please confirm this constant exists in routeNames.js
    // (matching '/customer-login'); swap for the literal path if not.
    navigate(RouteNames.CUSTOMER_LOGIN);
  };

  const sidebarElement = (
    <div className={cx('overlay')} onClick={onRequestClose}>
      <div className={cx('panel')} onClick={e => e.stopPropagation()}>
        <div className={cx('panel-header')}>
          <p className={cx('panel-title')}>My Account</p>
          <button type="button" className={cx('close-btn')} onClick={onRequestClose} aria-label="Close">
            <X size={35} style={{ width: 35, height: 35 }} />
          </button>
        </div>

        <nav className={cx('link-list')}>
          <Link to={RouteNames.ACCOUNT} className={cx('link')} onClick={onRequestClose}>
            <span className={cx('link-icon')}>
              <User size={17} />
            </span>
            My Account
          </Link>
          <Link to={RouteNames.USED_COUPONS_HISTORY} className={cx('link')} onClick={onRequestClose}>
            <span className={cx('link-icon')}>
              <Gift size={17} />
            </span>
            Used Coupons
          </Link>
          <Link to={RouteNames.FAVOURITES} className={cx('link')} onClick={onRequestClose}>
            <span className={cx('link-icon')}>
              <Heart size={17} />
            </span>
            Favourites
          </Link>
          <Link to={RouteNames.REFERRALS_CUSTOMERS} className={cx('link')} onClick={onRequestClose}>
            <span className={cx('link-icon')}>
              <Users size={17} />
            </span>
            My Referrals
          </Link>
          {/* <Link to={RouteNames.PREFERENCES} className={cx('link')} onClick={onRequestClose}>
            <span className={cx('link-icon')}>
              <Settings size={17} />
            </span>
            My Preferences
          </Link> */}

          <div className={cx('divider')} />

          <button type="button" className={cx('logout-link')} onClick={logout}>
            <span className={cx('link-icon')}>
              <LogOut size={17} />
            </span>
            Log Out
          </button>
        </nav>
      </div>
    </div>
  );

  if (isOpen) {
    return ReactDOM.createPortal(sidebarElement, target);
  }

  return null;
};

export default connect()(CustomerSidebar);